import React, { Suspense, useMemo, useState, useEffect, useCallback } from 'react';

import { APP_VERSION } from './version';
import { loadDeck, type CardConfig } from './lib/deck';

const cn = (...classes: (string | undefined | null | false)[]) => classes.filter(Boolean).join(' ');

function App() {
  // Generic Flow State
  const [flowData, setFlowData] = useState<Record<string, any>>({});

  // URL Query Parameters
  const [queryParams, setQueryParams] = useState<Record<string, string>>({});

  // Initialize Query Params
  useEffect(() => {
    const params = new URLSearchParams(window.location.search);
    const paramsObj: Record<string, string> = {};
    for (const [key, value] of params.entries()) {
      paramsObj[key] = value;
    }
    setQueryParams(paramsObj);
  }, []);

  // Determine Initial Deck ID from Path or Hostname
  const path = window.location.pathname.replace(/^\/|\/$/g, '');

  let defaultDeck = 'liflo-studio';
  if (typeof window !== 'undefined') {
    const hostname = window.location.hostname;
    if (hostname === 'mbisq.com' || hostname === 'www.mbisq.com') {
      defaultDeck = 'mbisq';
    }
  }

  const initialDeckId = path || defaultDeck;

  // Cards State (Raw Configs)
  const [cardConfigs, setCardConfigs] = useState<CardConfig[]>([]);
  const [loadedDecks, setLoadedDecks] = useState<Set<string>>(new Set());
  const [activeCardId, setActiveCardId] = useState<string | null>(null);

  // Load Initial Deck
  useEffect(() => {
    if (loadedDecks.size === 0) {
      loadDeck(initialDeckId, true).then(initialCards => {
        if (initialCards && initialCards.length > 0) {
          setCardConfigs(initialCards);
          setLoadedDecks(new Set([initialDeckId]));
          setActiveCardId(initialCards[0].id);
        }
      });
    }
  }, [initialDeckId, loadedDecks.size]);


  // Global Active State (Manual Toggle) -> Now Per-Card State
  const [activeStates, setActiveStates] = useState<Record<string, boolean>>({});

  const isCurrentActive = useMemo(() => {
    if (!activeCardId) return true;
    return activeStates[activeCardId] ?? true;
  }, [activeCardId, activeStates]);

  const toggleActive = useCallback(() => {
    if (!activeCardId) return;
    setActiveStates(prev => ({
      ...prev,
      [activeCardId]: !(prev[activeCardId] ?? true)
    }));
  }, [activeCardId]);

  // History Stack
  const [history, setHistory] = useState<string[]>([]);

  // Navigation Handler
  const navigateToCard = async (target: string, type?: string) => {
    if (type === 'connect') {
      const targetDeck = target;
      if (loadedDecks.has(targetDeck)) {
        const firstCard = cardConfigs.find(c => c.id.startsWith(`${targetDeck}-`));
        if (firstCard) {
          setHistory(prev => [...prev, activeCardId!]); // Push current
          setActiveCardId(firstCard.id);
        }
      } else {
        const loaded = await loadDeck(targetDeck, false);
        if (loaded && loaded.length > 0) {
          setCardConfigs(prev => {
            // Simple check to prevent duplicates
            const currentIds = new Set(prev.map(c => c.id));
            const filteredNew = loaded.filter(c => !currentIds.has(c.id));
            return [...prev, ...filteredNew];
          });
          setLoadedDecks(prev => new Set(prev).add(targetDeck));

          setHistory(prev => [...prev, activeCardId!]); // Push current
          setActiveCardId(loaded[0].id);
        }
      }
    } else if (type === 'link') {
      window.open(target, '_blank');
    } else {
      setHistory(prev => [...prev, activeCardId!]); // Push current
      setActiveCardId(target);
    }
  };

  const goBack = useCallback(() => {
    if (history.length === 0) return;
    const prevId = history[history.length - 1];
    setHistory(prev => prev.slice(0, -1));
    setActiveCardId(prevId);
  }, [history]);

  // Generic Data Handler
  const handleDataChange = (newData: Record<string, any>) => {
    setFlowData(prev => ({ ...prev, ...newData }));
  };

  // Dynamically generate lazy components
  const cards = useMemo(() => {
    if (!cardConfigs) {
      return [];
    }

    return cardConfigs.map((card) => {
      const Component = React.lazy(() =>
        import(`./components/${card.type}.tsx`)
          .then((module) => {
            const Component = module[card.type] || module.default;
            if (!Component) {
              throw new Error(`Component ${card.type} not found in module`);
            }
            return { default: Component };
          })
          .catch(err => {
            console.error(`Failed to load component ${card.type}`, err);
            return { default: () => <div className="text-red-500">Error loading {card.type}: {err.message}</div> };
          })
      );
      return { ...card, Component };
    });
  }, [cardConfigs]);

  // Get Current Card
  const activeCard = useMemo(() => {
    return cards.find(c => c.id === activeCardId);
  }, [cards, activeCardId]);

  // Find Front Card ID (first card with role='front', or first card of entire set)
  const frontCardId = useMemo(() => {
    if (cards.length === 0) return null;
    const frontCard = cards.find(c => c.role === 'front');
    return frontCard ? frontCard.id : cards[0].id;
  }, [cards]);

  // Authentication State
  const [isAuthenticated, setIsAuthenticated] = useState(false);

  const isFrontCard = activeCard?.role?.includes('front') || (activeCardId === frontCardId);
  const isSecureCard = activeCard?.role?.includes('secure');

  // Dynamic Browser Title & Meta
  useEffect(() => {
    if (cards.length > 0) {
      // Prioritize the front card's title
      const frontCard = cards.find(c => c.role?.includes('front'));
      const title = frontCard?.props?.title || "Liflo Studio";
      document.title = title;

      // Optional: Update meta description if needed (though mostly for SEO crawlers which see static HTML)
      // client-side updates affect valid history/bookmark state
    }
  }, [cards]);

  // Secure Band Click Handler
  const handleBandClick = () => {
    // navigateToCard handles history and loading
    navigateToCard('auth', 'connect');
  };

  const renderCard = (card: CardConfig & { Component: any }) => {
    const { id, Component } = card;

    // Construct Props
    const props: any = {
      id: card.id,         // **Pass ID for self-identification (e.g. deletion)**
      ...card.props,       // Static props from JSON
      ...queryParams,            // URL Query Params
      ...flowData,               // Dynamic Flow Data
      onData: handleDataChange,  // Callback for data updates
      isActive: isCurrentActive,  // **Pass Per-Card Active State**
      navigateTo: navigateToCard, // Pass Navigation
      goBack: goBack, // **Pass Back Navigation** (for explicit back requests)
      setAuthenticated: setIsAuthenticated // **Pass Auth Setter**
    };

    // Handle Transitions
    if (card.transitions) {
      props.actions = card.transitions.map((transition: { label: string; target: string; type?: string }) => ({
        label: transition.label,
        onClick: () => navigateToCard(transition.target, transition.type)
      }));

      // Convenience: if there's a 'Next' transition, pass onNext
      const nextTransition = card.transitions.find((t: { label: string }) => t.label === 'Next');
      if (nextTransition) {
        props.onNext = () => navigateToCard(nextTransition.target, nextTransition.type);
      }
    }

    const isMultiViewer = card.type === 'PoseViewerMultiCard';

    return (
      <div key={id} className="h-full w-full flex items-center justify-center animate-in fade-in duration-300 overflow-x-auto">
        {/* Positioning Wrapper */}
        <div
          className={cn(
            "relative w-[calc(100vw-16px)] h-[90dvh] mx-auto transition-all duration-500",
            isMultiViewer ? "max-w-[1280px]" : "max-w-[430px]"
          )}
        >

          {/* Card Content Frame (Clipped) */}
          <div className="h-full w-full bg-zinc-900 rounded-3xl overflow-hidden shadow-2xl border border-zinc-800 relative">
            {/* Component */}
            <Component {...props} />

            {/* Secure Overlay (Strap Band UI) */}
            {isSecureCard && !isAuthenticated && (
              <div
                onClick={handleBandClick}
                className="absolute inset-0 z-50 flex items-center justify-center cursor-pointer group overflow-hidden"
              >
                {/* Backdrop */}
                <div className="absolute inset-0 bg-black/60 backdrop-blur-[2px] transition-opacity group-hover:bg-black/50" />

                {/* Rotated Strap Band */}
                <div className="relative w-full bg-[#E5E7EB] shadow-2xl py-4 flex items-center justify-center border-y-4 border-dashed border-zinc-400 group-hover:scale-105 transition-all duration-500 ease-in-out origin-center">
                  <div className="flex items-center gap-4 px-12 py-2">
                    <div className="p-3 bg-zinc-900 rounded-full border-2 border-zinc-700 shadow-lg">
                      <svg xmlns="http://www.w3.org/2000/svg" width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" className="lucide lucide-lock text-zinc-100"><rect width="18" height="11" x="3" y="11" rx="2" ry="2" /><path d="M7 11V7a5 5 0 0 1 10 0v4" /></svg>
                    </div>
                    <div className="flex flex-col">
                      <span className="text-zinc-900 font-black text-2xl tracking-tighter uppercase leading-none">Secure Access</span>
                      <span className="text-zinc-600 font-medium text-xs tracking-widest uppercase">Tap to Unlock Content</span>
                    </div>
                  </div>

                  {/* Decorative 'Stitching' or texture lines if needed, implicitly handled by border-dashed */}
                </div>
              </div>
            )}

            {/* Dim Overlay when Inactive */}
            {!isCurrentActive && !isFrontCard && (
              <div className="absolute inset-0 bg-black/80 backdrop-blur-sm z-50 flex items-center justify-center pointer-events-none">
                <div className="text-zinc-500 font-mono text-sm">Inactive</div>
              </div>
            )}

            {/* GLOBAL BACK BUTTON */}
            {!isFrontCard && history.length > 0 && (
              <button
                onClick={goBack}
                className="absolute top-8 left-8 z-[60] p-2 bg-zinc-800/80 backdrop-blur-md rounded-full hover:bg-zinc-700 transition-colors border border-white/10 shadow-lg"
              >
                <svg xmlns="http://www.w3.org/2000/svg" width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" className="w-6 h-6 text-white"><path d="m12 19-7-7 7-7" /><path d="M19 12H5" /></svg>
              </button>
            )}
          </div>

          {/* Manual Active Toggle (Outside Top Right - No Overlap) */}
          {!isFrontCard && (
            <button
              onClick={toggleActive}
              className={`absolute right-0 bottom-full mb-0.5 z-[60] px-4 py-1.5 min-w-[60px] flex items-center justify-center rounded-full border-4 border-black shadow-lg transition-all ${isCurrentActive
                ? 'bg-green-500 text-white hover:bg-green-600'
                : 'bg-zinc-700 text-zinc-400 hover:bg-zinc-600'
                }`}
            >
              <span className="text-xs font-bold whitespace-nowrap">{isCurrentActive ? 'ACTIVE' : 'INACTIVE'}</span>
            </button>
          )}

          {/* Back to Front Card Button (Outside Top Left - No Overlap) */}
          {/* Replaced by Global Back Button inside card, but kept for "Home" functionality if history assumes linear. 
              Actually, "HOME" wipes history? Or is redundant? 
              User asked for "WhiteNoiseCard like back button", which is history back.
              The external HOME button goes to "Front". Let's keep it as a "Force Quit" option.
           */}
          {!isFrontCard && frontCardId && (
            <button
              onClick={() => {
                setHistory([]); // clear history on home
                navigateToCard(frontCardId);
              }}
              className="absolute left-0 bottom-full mb-0.5 z-[60] px-4 py-1.5 min-w-[60px] flex items-center justify-center rounded-full border-4 border-black shadow-lg transition-all bg-zinc-700 text-zinc-400 hover:bg-zinc-600 hover:text-white"
            >
              <span className="text-xs font-bold whitespace-nowrap">HOME</span>
            </button>
          )}
        </div>
      </div>
    );
  };

  return (
    <div className="h-[100dvh] w-full bg-black text-white overflow-hidden relative">
      <div className="h-full w-full relative">
        <Suspense fallback={<div className="h-full w-full flex items-center justify-center text-zinc-500 animate-pulse">Loading...</div>}>
          {activeCard && renderCard(activeCard)}
        </Suspense>
      </div>

      {/* Version Footer */}
      <div className="absolute bottom-2 right-4 text-zinc-600 text-xs pointer-events-none z-50">
        v{APP_VERSION}
      </div>
    </div>
  );
};

export default App;

